import React, { useCallback, useEffect, useState, useRef } from "react";
import _ from "lodash";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  useMantineTheme,
  Button,
  Center,
  Drawer,
  Stack,
  Text,
  Card,
  ActionIcon,
} from "@mantine/core";
import {
  IconPlus,
  IconClock,
  IconCalendar,
  IconChevronRight,
  IconRobot,
  IconWorld,
  IconAdjustments,
  IconGitBranch,
  IconCode,
  IconUserCheck,
  IconCirclePlus,
  IconNetwork,
  IconMail,
} from "@tabler/icons";

import ReactJson from "react-json-view";

import WorkFlowNodeSetting from "./workFlowNodeSetting";
import WorkFlowNodesPanel from "./workflowNodesPanel";
import NODE_TYPES from "./nodeTypes";
import ColorNode from "./nodes/colorNode";
import LoopNode from "./nodes/loopNode";

const initialPosition = { x: 30, y: 20 };

const WorkflowDiagram = ({
  form,
  name,
  width = "1220px",
  height = "800px",
  layout = "horizontal", // 'horizontal' or 'vertical'
  ...props
}) => {
  const defaultNode = {
    id: "1",
    position: initialPosition,
    sourcePosition: layout === "vertical" ? "top" : "right",
    targetPosition: layout === "vertical" ? "bottom" : "left",
    data: { label: "Start", type: "MANUAL" },
    type: "TRIGGER",
  };

  const [showAddNodeDrawer, setShowAddNodeDrawer] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodeForConnection, setSelectedNodeForConnection] =
    useState(null);
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState([
    {
      id: "1",
      position: initialPosition,
      sourcePosition: layout === "vertical" ? "top" : "right",
      targetPosition: layout === "vertical" ? "bottom" : "left",
      data: { label: "Start", type: "MANUAL" },
      type: "TRIGGER",
    },
    // //color node
    // {
    //   id: "2",
    //   position: { x: 300, y: 20 },
    //   data: { label: "Color Picker", type: "COLOR_PICKER" },
    //   type: "COLOR_PICKER",
    // },
    // {
    //   id: "3",
    //   position: { x: 350, y: 20 },
    //   data: { label: "Loop", type: "LOOP", inputArray: "emailIds" },
    //   type: "LOOP",
    // },
  ]);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState([]);
  const theme = useMantineTheme();
  const initialLoad = useRef(true);

  // Custom change handlers that update both state and form
  const onNodesChange = useCallback(
    (changes) => {
      onNodesChangeInternal(changes);
    },
    [onNodesChangeInternal]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      onEdgesChangeInternal(changes);
    },
    [onEdgesChangeInternal]
  );

  useEffect(() => {
    // Load nodes and edges from form values when they become available
    if (form.values.nodes.length > 0 && initialLoad.current) {
      setNodes(form.values.nodes);
      setEdges(form.values.edges);
      initialLoad.current = false;
    }
  }, [form.values.nodes, form.values.edges, setNodes, setEdges]);

  // Update form value whenever nodes or edges change
  useEffect(() => {
    if (!form || !name) return;
    if (initialLoad.current) return;
    form.setFieldValue("nodes", nodes);
    form.setFieldValue("edges", edges);
  }, [nodes, edges]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodesDelete = useCallback(
    (deleted) => {
      setNodes((nds) => (nds || []).filter((node) => !deleted.includes(node)));
      // Also remove any connected edges
      setEdges((eds) =>
        (eds || []).filter(
          (edge) =>
            !deleted.some(
              (node) => edge.source === node.id || edge.target === node.id
            )
        )
      );
    },
    [setNodes, setEdges]
  );

  const handleNodeDoubleClick = useCallback((event, node) => {
    setSelectedNode(node);
    setModalOpen(true);
  }, []);

  const handleNodeClick = useCallback((event, node) => {
    setSelectedNodeForConnection(node);
  }, []);

  const handleNodeSettingSave = (updatedNode) => {
    setNodes((nds) =>
      (nds || []).map((n) => (n.id === updatedNode.id ? updatedNode : n))
    );
    setModalOpen(false);
  };

  const handleAddNode = (nodeTypeKey) => {
    const nodeConfig = NODE_TYPES.find((node) => node.type === nodeTypeKey);
    if (!nodeConfig) {
      console.error(`Node type ${nodeTypeKey} not found`);
      return;
    }
    const newNodeId = `${nodeTypeKey}-${Date.now()}`;
    let position;

    // Determine which node to position relative to
    let referenceNode;
    if (selectedNodeForConnection) {
      referenceNode = selectedNodeForConnection;
    } else if (nodes.length > 0) {
      referenceNode = nodes[nodes.length - 1];
    }

    if (layout === "vertical") {
      position = referenceNode
        ? { x: referenceNode.position.x, y: referenceNode.position.y + 120 }
        : { x: 100, y: 100 };
    } else {
      // horizontal (default)
      position = referenceNode
        ? { x: referenceNode.position.x + 200, y: referenceNode.position.y }
        : { x: 100, y: 100 };
    }
    const newNode = {
      id: newNodeId,
      position,
      data: { ...nodeConfig.data, label: nodeConfig.label },
      type: nodeConfig.type,
      sourcePosition: layout === "vertical" ? "top" : "right",
      targetPosition: layout === "vertical" ? "bottom" : "left",
    };
    setNodes((nds) => {
      const updatedNodes = [...(nds || []), newNode];
      // Connect to selected node if available, otherwise connect to last node
      if (nds && nds.length > 0) {
        let sourceNodeId;
        if (selectedNodeForConnection) {
          sourceNodeId = selectedNodeForConnection.id;
        } else {
          sourceNodeId = nds[nds.length - 1].id;
        }

        setEdges((eds) => [
          ...(eds || []),
          {
            id: `e${sourceNodeId}-${newNodeId}`,
            source: sourceNodeId,
            target: newNodeId,
          },
        ]);
      }
      return updatedNodes;
    });

    // Set the newly added node as selected for future connections
    setSelectedNodeForConnection(newNode);
    setShowAddNodeDrawer(false);
  };

  //Add first node trigger nodes
  const addFirstNode = useCallback(() => {
    setNodes([
      {
        id: "1",
        position: initialPosition,
        sourcePosition: layout === "vertical" ? "top" : "right",
        targetPosition: layout === "vertical" ? "bottom" : "left",
        data: { label: "Start", type: "MANUAL" },
        type: "TRIGGER",
      },
      //color node
      {
        id: "2",
        position: { x: 300, y: 20 },
        data: { label: "Color Picker", type: "COLOR_PICKER" },
        type: "COLOR_PICKER",
      },
    ]);
  }, [setNodes]);

  const customNodeTypes = {
    // ...NODE_TYPES,
    COLOR_PICKER: ColorNode,
    LOOP: LoopNode,
  };

  return (
    <div style={{ width: width, height: height, position: "relative" }}>
      {/* Floating add node button */}
      <div style={{ position: "absolute", top: 24, right: -56, zIndex: 10 }}>
        <ActionIcon
          variant="light"
          color="gray"
          radius="xl"
          size="md"
          onClick={() => setShowAddNodeDrawer(true)}
        >
          <IconCirclePlus size={20} />
        </ActionIcon>
      </div>

      {/* <ReactJson
        src={form.values}
        style={{ backgroundColor: "white" }}
        collapsed
      /> */}

      <ReactFlow
        key={
          (nodes ?? []).map((n) => n.id).join("-") +
          (edges ?? []).map((e) => e.id).join("-")
        }
        nodes={nodes ?? []}
        edges={edges ?? []}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodesDelete={onNodesDelete}
        colorMode={theme.colorScheme}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        nodeTypes={customNodeTypes}
        fitView={true}
        minZoom={0.1}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 0.2 }}
        proOptions={{ hideAttribution: true }}
      >
        <Controls />
        <MiniMap />
        <Background variant="dots" gap={12} size={1} />
      </ReactFlow>

      <WorkFlowNodeSetting
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        node={selectedNode}
        onSave={handleNodeSettingSave}
        form={form}
      />
      <WorkFlowNodesPanel
        opened={showAddNodeDrawer}
        onClose={() => setShowAddNodeDrawer(false)}
        onAddNode={handleAddNode}
        NODE_TYPES={NODE_TYPES}
      />
    </div>
  );
};

export default WorkflowDiagram;
